import { useState } from 'react';
import Modal from '../common/Modal';
import Spinner from '../common/Spinner';
import { useUpgradePlan, useUpgradePreview } from '../../hooks/useBilling';
import { ApiRequestError } from '../../api/errors';
import { formatDateShort } from '../../utils/date';
import type { UpgradeOption } from '../../types';
import { describePlan } from '../../utils/planDescriptions';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  option: UpgradeOption;
}

// Plan-row prices: trim trailing zeros (matches AddSubscriptionModal)
function formatPlanPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

// Charge amounts: always two decimals
function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
}

export default function UpgradePlanModal({ isOpen, onClose, option }: Props) {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [priceChanged, setPriceChanged] = useState(false);
  const [isVerifyingPrice, setIsVerifyingPrice] = useState(false);
  const upgrade = useUpgradePlan();

  const result = upgrade.isSuccess ? upgrade.data : undefined;
  // legacy_checkout success redirects to Stripe — no in-modal success state,
  // but lock the button so re-clicks (during navigation or after a
  // bfcache-back) can't mint duplicate checkout sessions
  const isRedirecting = result?.flow === 'legacy_checkout';
  const isComplete = result !== undefined && result.flow !== 'legacy_checkout';

  // possible_desync: the upgrade may have partially applied server-side and
  // the team was alerted — re-confirming would replay the same outcome
  const isDesync =
    upgrade.error instanceof ApiRequestError &&
    upgrade.error.slug === 'possible_desync';

  const preview = useUpgradePreview(
    option.subscription_id,
    selectedPlan,
    option.flow_type === 'stripe_instant' && !isComplete
  );

  const handleClose = () => {
    if (upgrade.isPending || isVerifyingPrice) return;
    upgrade.reset();
    setSelectedPlan(null);
    setPriceChanged(false);
    onClose();
  };

  const handleSelectPlan = (planName: string) => {
    setSelectedPlan(planName);
    setPriceChanged(false);
  };

  const handleSubmit = async () => {
    if (!selectedPlan) return;

    if (option.flow_type === 'stripe_instant') {
      // Re-verify the quoted charge right before charging — the preview can
      // go stale while the modal sits open (e.g. the renewal date passes)
      const shownAmount = preview.data?.amount_due_today;
      setPriceChanged(false);
      setIsVerifyingPrice(true);
      try {
        const fresh = await preview.refetch();
        if (fresh.error || !fresh.data) {
          // Preview error box renders via preview.isError; don't charge
          return;
        }
        if (
          shownAmount === undefined ||
          Math.abs(fresh.data.amount_due_today - shownAmount) > 0.01
        ) {
          // Amount drifted from what the customer saw — the preview box has
          // re-rendered with the new amount; require an explicit re-confirm
          setPriceChanged(true);
          return;
        }
      } finally {
        setIsVerifyingPrice(false);
      }
    }

    upgrade.mutate({
      subscription_id: option.subscription_id,
      plan_name: selectedPlan,
    });
  };

  const submitLabel =
    option.flow_type === 'stripe_instant'
      ? 'Confirm Upgrade'
      : option.flow_type === 'legacy_checkout'
        ? 'Continue to Checkout'
        : 'Request Upgrade';

  const submitDisabled =
    upgrade.isPending ||
    isVerifyingPrice ||
    isRedirecting ||
    isDesync ||
    !selectedPlan ||
    (option.flow_type === 'stripe_instant' &&
      (!preview.data || preview.isError));

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Upgrade Plan">
      {isComplete && result ? (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <p className="text-sm text-green-800">
              {result.flow === 'request'
                ? "Request received — we'll be in touch shortly."
                : 'Upgrade complete — your container is being resized.'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
          >
            Close
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-gray-700">
            Upgrade <span className="font-medium">{option.hostname}</span> from{' '}
            <span className="font-medium">{option.current_plan}</span>. Your{' '}
            {option.frequency} billing interval stays the same.
          </p>

          {/* Plan Selection — eligible (higher) tiers only, current interval */}
          <div className="space-y-2">
            {option.eligible_plans.map((plan) => (
              <label
                key={plan.plan_name}
                className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedPlan === plan.plan_name
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="upgrade-plan"
                    value={plan.plan_name}
                    checked={selectedPlan === plan.plan_name}
                    onChange={() => handleSelectPlan(plan.plan_name)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">
                      {plan.plan_name}
                    </div>
                    {describePlan(plan.plan_name) && (
                      <div className="text-sm text-gray-500">
                        {describePlan(plan.plan_name)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-gray-900">
                    {formatPlanPrice(plan.price, plan.currency)}
                  </div>
                  <div className="text-sm text-gray-500">
                    /{option.frequency === 'monthly' ? 'mo' : 'yr'}
                  </div>
                </div>
              </label>
            ))}
          </div>

          {/* stripe_instant: proration preview */}
          {option.flow_type === 'stripe_instant' && selectedPlan && (
            preview.isPending ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner size="sm" />
                <span>Calculating your prorated charge...</span>
              </div>
            ) : preview.isError ? (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800">
                  Failed to calculate the upgrade charge. Please try again or
                  contact support.
                </p>
              </div>
            ) : preview.data ? (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-900">
                  You'll be charged{' '}
                  <span className="font-medium">
                    {formatMoney(preview.data.amount_due_today, preview.data.currency)}
                  </span>{' '}
                  today. Your renewal date stays{' '}
                  <span className="font-medium">
                    {formatDateShort(preview.data.renewal_date)}
                  </span>.
                </p>
              </div>
            ) : null
          )}

          {/* stripe_instant: the pre-charge re-verify found a different amount */}
          {option.flow_type === 'stripe_instant' && priceChanged && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-sm text-amber-800">
                The charge amount has been updated — please review and confirm
                again.
              </p>
            </div>
          )}

          {/* legacy_checkout: migration explanation */}
          {option.flow_type === 'legacy_checkout' && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-900">
                Your billing will move to Stripe as part of this upgrade. Your
                remaining paid time converts to credit before billing starts on
                your new plan — the exact number of credited days is shown on
                the checkout page before you pay.
              </p>
            </div>
          )}

          {/* request: manual review explanation */}
          {option.flow_type === 'request' && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-900">
                This upgrade needs a quick review by our team. Submit a request
                and we'll complete the upgrade and follow up with you shortly.
              </p>
            </div>
          )}

          {upgrade.isError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">
                {upgrade.error instanceof Error
                  ? upgrade.error.message
                  : 'Failed to upgrade plan. Please try again or contact support.'}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleClose}
              disabled={upgrade.isPending || isVerifyingPrice}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitDisabled}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isDesync ? (
                'Upgrade pending review'
              ) : upgrade.isPending || isVerifyingPrice ? (
                <>
                  <Spinner size="sm" className="border-white border-t-transparent" />
                  <span>Processing...</span>
                </>
              ) : isRedirecting ? (
                'Redirecting...'
              ) : (
                submitLabel
              )}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
